/* eslint-disable react/no-danger, react/require-default-props */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import Loader from 'components/atoms/loader';
import { consult } from 'store/modules/juridico_facil_store/actions';
import { ApplicationState } from 'store';
import { JuridicoFacilState } from 'store/modules/juridico_facil_store/types';

import * as J from './styles';
import { referenciaDecisao, semHtml, siglaTribunal } from './Resultados';

interface DetalheProps {
  id: string;
  fonte?: string;
  onVoltar: () => void;
  onAbrir: (id: number, fonte?: string) => void;
}

// Labels das abas vêm crus do JusBrasil (conjunto aberto: tribunal novo pode
// trazer label novo); os conhecidos têm tradução, o resto é humanizado
const ROTULOS_CONHECIDOS: Record<string, string> = {
  INTEIRO_TEOR: 'Inteiro teor',
  RELATORIO_VOTO: 'Relatório e voto',
  CERTIDAO_JULGAMENTO: 'Certidão de julgamento',
  FATOS: 'Fatos',
  FATOS_SIMPLES: 'Fatos simplificados',
  EMENTA: 'Ementa',
  ACORDAO: 'Acórdão',
  DECISAO: 'Decisão',
};

const humanizarLabel = (label?: string | null) =>
  ROTULOS_CONHECIDOS[(label || '').toUpperCase()] ||
  (label || 'Texto')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (letra) => letra.toUpperCase());

const Detalhe: React.FC<DetalheProps> = ({ id, fonte, onVoltar, onAbrir }) => {
  const dispatch = useDispatch();
  const { juridico, request } = useSelector<ApplicationState, JuridicoFacilState>(
    (state) => state.session.juridico,
  );
  const [aba, setAba] = useState(0);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    setAba(0);
    dispatch(consult(id, fonte));
  }, [id, fonte]);

  const item = juridico?.conteudo?.[0];
  const doJusBrasil = item?.fonte === 'jusbrasil';
  const textos = juridico?.textos || [];
  const referencias = (juridico?.referencia_legal || []).filter((ref) => ref.referencia_legal);
  const relacionados = juridico?.sugestoes || [];
  const data = item?.data_sessao ? moment(item.data_sessao).format('DD/MM/YYYY') : '';

  const copiarCitacao = async () => {
    if (!item) return;
    const partes = [
      item.tipo,
      referenciaDecisao(item.acordao, item.fonte, item.tipo),
      data ? `sessão de ${data}` : '',
      item.autor_da_tese ? `Rel. ${item.autor_da_tese}` : '',
      siglaTribunal(item.court),
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(`“${semHtml(item.enunciado)}” — ${partes.join(', ')}`);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      // clipboard indisponível — ignora
    }
  };

  const renderConteudo = () => {
    if (request?.consult?.loading) return <Loader />;
    if (!item) return <J.Vazio>Conteúdo não encontrado.</J.Vazio>;

    return (
      <article>
        <header>
          <h3>
            {item.tipo}
            {!!item.court && <em>{siglaTribunal(item.court)}</em>}
          </h3>
          <div className="chips">
            {[item.area, item.tema, item.subtema].filter(Boolean).map((chip) => (
              <span key={chip}>{chip}</span>
            ))}
          </div>
        </header>

        {item.tipo !== 'Dica Legal' && (
          <dl>
            {!!item.acordao && (
              <div>
                <dt>{doJusBrasil ? 'Processo' : 'Acórdão'}</dt>
                <dd>{item.acordao.replace(/^ac[óo]rd[ãa]o:?\s*/i, '')}</dd>
              </div>
            )}
            {!!data && (
              <div>
                <dt>Data da sessão</dt>
                <dd>{data}</dd>
              </div>
            )}
            {!!item.autor_da_tese && (
              <div>
                <dt>Relator</dt>
                <dd>{item.autor_da_tese.replace(/^relator:?\s*/i, '')}</dd>
              </div>
            )}
            {!!item.tipo_de_processo && (
              <div>
                <dt>Tipo de processo</dt>
                <dd>{item.tipo_de_processo}</dd>
              </div>
            )}
            {!!item.grau && (
              <div>
                <dt>Grau</dt>
                <dd>{item.grau}</dd>
              </div>
            )}
            {!!item.url && (
              <div>
                <dt>Fonte</dt>
                <dd>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    Ver no JusBrasil
                  </a>
                </dd>
              </div>
            )}
          </dl>
        )}

        {!!item.dica_em_video && (
          <div className="video">
            <iframe
              title="Dica em vídeo"
              src={`https://www.youtube-nocookie.com/embed/${item.dica_em_video.replace(
                /https:\/\/youtu.be\//,
                '',
              )}`}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        {!!item.tags && (
          <p className="indexadores">
            <strong>Outros indexadores:</strong> {item.tags}
          </p>
        )}

        {!!item.enunciado && (
          <section>
            <h4>Enunciado</h4>
            <div dangerouslySetInnerHTML={{ __html: item.enunciado }} />
            {item.tipo !== 'Dica Legal' && (
              <button type="button" className="citar" onClick={copiarCitacao}>
                <i className="material-icons">{copiado ? 'check' : 'content_copy'}</i>
                {copiado ? 'Citação copiada' : 'Copiar citação'}
              </button>
            )}
          </section>
        )}

        {!!item.conteudo && (
          <section>
            <h4>Excerto</h4>
            <div
              className={doJusBrasil ? 'pre' : ''}
              dangerouslySetInnerHTML={{ __html: item.conteudo }}
            />
          </section>
        )}

        {textos.length > 0 && (
          <section>
            <h4>Texto integral</h4>
            <div className="abas">
              {textos.map((texto, indice) => (
                <button
                  key={texto.id}
                  type="button"
                  className={indice === aba ? 'ativa' : ''}
                  onClick={() => setAba(indice)}
                >
                  {humanizarLabel(texto.label)}
                </button>
              ))}
            </div>
            <div className="pre">{textos[aba]?.conteudo}</div>
          </section>
        )}

        {referencias.length > 0 && (
          <section>
            <h4>Referência legal</h4>
            {referencias.map((ref) => (
              <p key={ref.id}>{ref.referencia_legal}</p>
            ))}
          </section>
        )}

        {relacionados.length > 0 && (
          <section>
            <h4>Enunciados relacionados</h4>
            <ul className="relacionados">
              {relacionados.map((sugestao) => (
                <li key={sugestao.id}>
                  <button type="button" onClick={() => onAbrir(sugestao.id)}>
                    <span>{semHtml(sugestao.enunciado)}</span>
                    <small>
                      {[
                        referenciaDecisao(sugestao.acordao, undefined, 'Jurisprudência'),
                        sugestao.data_sessao
                          ? moment(sugestao.data_sessao).format('DD/MM/YYYY')
                          : '',
                        sugestao.subtema,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    );
  };

  return (
    <J.Detalhe>
      <button type="button" className="voltar" onClick={onVoltar}>
        <i className="material-icons">arrow_back</i>
        Voltar aos resultados
      </button>
      {renderConteudo()}
    </J.Detalhe>
  );
};

export default Detalhe;
